import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import logoMarkRed from '@/assets/branding/logo-mark-red.svg';
import logoLockupRed from '@/assets/branding/logo-lockup-red.svg';
import { useAuth } from '@/contexts/AuthContext';
import { EnvironmentBadge } from '@/components/Scope/EnvironmentBadge';
import { useScope } from '@/contexts/ScopeContext';
import { CommandPalette } from '@/components/CommandPalette/CommandPalette';
import NotificationButton from './NotificationButton';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  User,
  HelpCircle,
  LogOut,
  Building2,
  Key,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  Search,
  Menu,
} from 'lucide-react';

interface HeaderRefactoredProps {
  onKillSwitch?: () => void;
  onMobileMenuToggle?: () => void;
  sidebarCollapsed?: boolean;
  onSidebarToggle?: () => void;
  className?: string;
}


export default function HeaderRefactored({ onKillSwitch, onMobileMenuToggle, sidebarCollapsed = false, onSidebarToggle, className }: HeaderRefactoredProps) {
  const [, setLocation] = useLocation();
  const { user, logout } = useAuth();
  const {
    organizations = [],
    organization,
    portfolio,
    setOrganization,
    setPortfolio,
  } = useScope();
  const isMobile = useIsMobile(1024);

  // State
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isCondensed, setIsCondensed] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Get current data from hierarchy
  const currentPortfolios = organization?.portfolios || [];

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(media.matches);
    updatePreference();
    if (media.addEventListener) {
      media.addEventListener('change', updatePreference);
      return () => media.removeEventListener('change', updatePreference);
    }
    media.addListener(updatePreference);
    return () => media.removeListener(updatePreference);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (typeof window === 'undefined') return;
      setIsCondensed(window.scrollY > 24);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const headerTransitionClass = prefersReducedMotion ? '' : 'transition-all duration-200';
  const headerHeightClass = isCondensed ? 'h-12' : 'h-14';
  const iconButtonClass = 'h-11 w-11 rounded-full focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red';

  return (
    <>
      <TooltipProvider>
        <header className={cn(
          'sticky top-0 z-50 bg-white border-b border-slate-200',
          headerTransitionClass,
          isCondensed && 'shadow-sm',
          className
        )}>
          <div className={cn('flex items-center w-full', headerHeightClass, headerTransitionClass)}>
            {/* Logo section aligned with sidebar width */}
            <div
              className={cn(
                'flex items-center',
                prefersReducedMotion ? '' : 'transition-all duration-200 ease-out',
                sidebarCollapsed ? "lg:w-[76px] justify-center" : "lg:w-[240px] justify-start"
              )}
              style={{
                borderRight: isMobile ? 'none' : '1px solid var(--sidebar-divider)'
              }}
            >
              <div
                className={cn(
                  "flex items-center gap-3 w-full",
                  sidebarCollapsed ? "px-3 justify-center" : "px-4"
                )}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      href="/overview"
                      aria-label="Rugira — Home"
                      className={cn(
                        'flex items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-brand-red',
                        sidebarCollapsed
                          ? 'h-11 w-11 justify-center'
                          : 'h-11 px-4 gap-3'
                      )}
                    >
                      <img
                        src={sidebarCollapsed || isMobile ? logoMarkRed : logoLockupRed}
                        alt=""
                        aria-hidden="true"
                        className={cn(
                          sidebarCollapsed || isMobile ? "h-8 w-auto" : "h-8 w-auto lg:h-9 max-w-[176px]"
                        )}
                      />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent align="start">
                    Rugira — Home
                  </TooltipContent>
                </Tooltip>

                {onMobileMenuToggle && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onMobileMenuToggle}
                    className={cn(iconButtonClass, 'lg:hidden p-2')}
                    aria-label="Toggle navigation menu"
                  >
                    <Menu className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Sidebar collapse button - right of separator */}
            <div className="hidden lg:flex items-center">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onSidebarToggle}
                    className="ml-2 p-2 h-8 w-8 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-brand-red"
                    aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                  >
                    {sidebarCollapsed ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronLeft className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Main header content */}
            <div className={cn('flex items-center flex-1 px-4 gap-4', headerTransitionClass)}>
              {/* Context chips */}
              <div className="flex items-center gap-3">
                {/* Breadcrumb Navigation */}
                <nav className="hidden sm:flex items-center" aria-label="Breadcrumb">
                  {/* Organization Chip */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 text-xs font-medium bg-slate-50/50 border border-slate-200/50 hover:bg-slate-100/50 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-brand-red rounded-md cursor-pointer"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        <span className="max-w-[140px] truncate">
                          {organization?.name || 'Organization'}
                        </span>
                        <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuLabel>Select Organization</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {organizations.map((org) => (
                        <DropdownMenuItem
                          key={org.id}
                          onClick={() => {
                            setOrganization(org);
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>{org.name}</span>
                            {organization?.id === org.id && (
                              <Badge variant="outline" className="text-xs">Active</Badge>
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <span className="mx-2 text-slate-400 text-xs select-none" aria-hidden="true">›</span>

                  {/* Desk Chip */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 text-xs font-medium bg-slate-50/50 border border-slate-200/50 hover:bg-slate-100/50 focus:outline-none focus:ring-2 focus:ring-offset-0 focus:ring-brand-red rounded-md cursor-pointer"
                        aria-haspopup="true"
                        aria-expanded="false"
                      >
                        <span className="max-w-[140px] truncate">
                          {portfolio?.name || 'Desk'}
                        </span>
                        <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuLabel>Select Desk</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {currentPortfolios.map((p: any) => (
                        <DropdownMenuItem
                          key={p.id}
                          onClick={() => {
                            setPortfolio(p);
                          }}
                          className="cursor-pointer"
                        >
                          <div className="flex items-center justify-between w-full">
                            <span>{p.name}</span>
                            {portfolio?.id === p.id && (
                              <Badge variant="outline" className="text-xs">Active</Badge>
                            )}
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </nav>

                {/* Environment Badge - positioned to the right of breadcrumbs */}
                <div className="hidden sm:block ml-3">
                  <EnvironmentBadge />
                </div>
              </div>

            {/* Spacer */}
            <div className="flex-1"></div>

            {/* User Actions */}
            <div className={cn('flex items-center gap-3 ml-auto', headerTransitionClass)}>
              {/* Omni-search */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(iconButtonClass, 'text-slate-600 hover:text-slate-900')}
                    onClick={() => setCommandPaletteOpen(true)}
                    aria-label="Open search"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Search <kbd className="ml-1 px-1 py-0.5 text-xs bg-slate-200 rounded">⌘K</kbd></p>
                </TooltipContent>
              </Tooltip>

              {/* Notifications */}
              <NotificationButton buttonClassName={iconButtonClass} iconClassName="h-5 w-5" />

              {/* Profile */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(iconButtonClass, 'rounded-full')}
                    aria-label="Profile menu"
                  >
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name || 'User'}</p>
                      <p className="text-xs text-slate-500">{user?.email || 'user@example.com'}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation('/profile')} className="cursor-pointer">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation('/api-keys')} className="cursor-pointer">
                    <Key className="mr-2 h-4 w-4" />
                    API Keys
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation('/organization')} className="cursor-pointer">
                    <Building2 className="mr-2 h-4 w-4" />
                    Organization
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation('/help')} className="cursor-pointer">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Help
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          </div>
        </header>
      </TooltipProvider>

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        fullScreen={isMobile}
      />
    </>
  );
}
